import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import {
  useListSupportMessagesQuery,
  usePostSupportMessageMutation,
  type SupportMessageRow,
} from "@/features/accounts/accountsApi";
import { formatSupportWhen, isClosedSupportStatus } from "./SupportCasesPanel";
import SupportCsatSummary from "./SupportCsatSummary";

function authorLabel(msg: SupportMessageRow): string {
  const t = String(msg.authorType || "").toLowerCase();
  if (t === "sa") return msg.authorDisplayName || "KrossKast Support";
  if (t === "ma") return "Client";
  if (t === "email") return msg.authorDisplayName || "Customer (email)";
  if (t === "system") return "System";
  return msg.authorDisplayName || "Participant";
}

/**
 * Live refresh (Owner QA: new inbound messages required a manual page
 * reload). Smallest canonical mechanism: RTK Query polling on the already
 * mounted messages query while the conversation is expanded, paused when the
 * tab is unfocused, plus refetch-on-focus (store has setupListeners). No new
 * realtime subsystem.
 *
 * Owner QA FIX 4 (KK-294667): a CLOSED Case is not static — the Client's CSAT
 * arrives after closure. The earlier "closed → no polling" rule left an
 * already-expanded closed thread showing "not yet rated" until manual Refresh.
 * Now: poll while open, AND while closed until the CSAT response is present;
 * additionally refetch immediately when the list row's `csatSubmittedAt`
 * changes (the list poll noticed the rating before this thread did).
 */
export const SUPPORT_THREAD_POLL_MS = 15_000;

interface SupportCaseThreadProps {
  masterAccountId: string;
  ticketId: string;
  /** Owner lifecycle: Closed is FINAL — no staff composer on a Closed Case. */
  closed?: boolean;
  /** From the list row; a change means the Client submitted CSAT → refetch thread. */
  csatSubmittedAt?: string | null;
}

export default function SupportCaseThread({
  masterAccountId,
  ticketId,
  closed = false,
  csatSubmittedAt = null,
}: SupportCaseThreadProps) {
  const [draft, setDraft] = useState("");
  // A closed Case with its CSAT already recorded is the only truly static state.
  const [settled, setSettled] = useState(false);
  const { data, isFetching, isError, error, refetch } =
    useListSupportMessagesQuery(
      { masterAccountId, ticketId },
      {
        pollingInterval: settled ? 0 : SUPPORT_THREAD_POLL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: !settled,
      },
    );
  const [postMessage, postState] = usePostSupportMessageMutation();

  const messages = data?.messages || [];
  // Server projection is authoritative if the list row was stale.
  const isClosed = closed || isClosedSupportStatus(data?.supportTicket?.status);
  const csatRecorded = Number.isFinite(Number(data?.csat?.rating));
  useEffect(() => {
    setSettled(isClosed && csatRecorded);
  }, [isClosed, csatRecorded]);

  // List row learned of a CSAT submission (or closure) → converge this thread now.
  const lastCsatSeenRef = useRef<string | null>(csatSubmittedAt);
  useEffect(() => {
    if (csatSubmittedAt && csatSubmittedAt !== lastCsatSeenRef.current) {
      lastCsatSeenRef.current = csatSubmittedAt;
      void refetch();
    }
  }, [csatSubmittedAt, refetch]);
  const lastClosedRef = useRef<boolean>(closed);
  useEffect(() => {
    if (closed && !lastClosedRef.current) void refetch();
    lastClosedRef.current = closed;
  }, [closed, refetch]);

  const onSend = async () => {
    const bodyText = draft.trim();
    if (!bodyText || isClosed) return;
    try {
      await postMessage({ masterAccountId, ticketId, bodyText }).unwrap();
      setDraft("");
    } catch {
      /* RTK error surfaced via postState */
    }
  };

  return (
    <div className="mt-3" data-testid={`support-thread-${ticketId}`}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: 11 }}
        >
          Conversation
        </div>
        <button
          type="button"
          className="btn btn-link btn-sm p-0 align-baseline"
          onClick={() => void refetch()}
          disabled={isFetching}
          title={
            settled
              ? "Refresh conversation"
              : `Refresh now (auto-refreshes every ${SUPPORT_THREAD_POLL_MS / 1000}s)`
          }
          data-testid={`support-thread-refresh-${ticketId}`}
        >
          {isFetching && data ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {isFetching && !data ? (
        <div className="d-flex align-items-center gap-2 small text-muted">
          <Spinner animation="border" size="sm" /> Loading thread…
        </div>
      ) : null}
      {isError ? (
        <Alert variant="warning" className="py-2 small mb-2">
          {(error as { data?: { message?: string } })?.data?.message ||
            (error as { message?: string })?.message ||
            "Failed to load conversation"}{" "}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </Alert>
      ) : null}
      <div
        className="d-flex flex-column gap-2 mb-2"
        style={{ maxHeight: 280, overflowY: "auto" }}
      >
        {messages.length === 0 && !isFetching ? (
          <div className="small text-muted">No messages yet.</div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.messageId}
              className="rounded p-2 small"
              style={{
                background:
                  String(msg.authorType).toLowerCase() === "sa"
                    ? "#eef6ff"
                    : "#f7f7f7",
              }}
            >
              <div className="d-flex justify-content-between gap-2 mb-1">
                <strong>{authorLabel(msg)}</strong>
                <span className="text-muted">
                  {formatSupportWhen(msg.createdAt)}
                </span>
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{msg.bodyText || "—"}</div>
              {msg.quotedHistoryTrimmed ? (
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  Quoted email history trimmed (original retained).
                </div>
              ) : null}
              {msg.attachmentDisposition ? (
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  Attachments were not ingested ({msg.attachmentDisposition}).
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
      {isClosed ? (
        <>
          <div
            className="small rounded p-2 border bg-light"
            data-testid={`support-thread-closed-${ticketId}`}
          >
            This Support Case is closed. Closed is final — the Client opens a new
            Support Case for anything further; email replies to this Case receive
            automated closed-case guidance and are not added here.
          </div>
          <SupportCsatSummary csat={data?.csat ?? null} ticketId={ticketId} />
        </>
      ) : (
        <>
          <Form.Control
            as="textarea"
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Reply to this Support Case…"
            className="mb-2"
            disabled={postState.isLoading}
          />
          {postState.isError ? (
            <Alert variant="danger" className="py-1 small mb-2">
              {(postState.error as { data?: { message?: string } })?.data
                ?.message ||
                (postState.error as { message?: string })?.message ||
                "Failed to send reply"}
            </Alert>
          ) : null}
          <Button
            size="sm"
            variant="primary"
            disabled={postState.isLoading || !draft.trim()}
            onClick={() => void onSend()}
          >
            {postState.isLoading ? "Sending…" : "Send reply"}
          </Button>
        </>
      )}
    </div>
  );
}
